import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mic, X, Save, Users, UserPlus } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  claimWeeklySlot,
  createNonMemberSlot,
  releaseSpeakerSlot,
  updateSpeakerTopic,
  useWeeklySpeakers,
  speakerKey,
  type SlotType,
} from "@/hooks/useWeeklySpeakers";

const WEEKS_AHEAD = 12;

const weekdayFor = (chapter: "washington" | "union") =>
  chapter === "washington" ? 3 : 4;

const toIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const generateUpcomingDates = (chapter: "washington" | "union"): string[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dates: string[] = [];

  // Washington meets bi-weekly: 2nd & 4th Wednesday of each month
  if (chapter === "washington") {
    let year = today.getFullYear();
    let month = today.getMonth();
    while (dates.length < WEEKS_AHEAD) {
      const wednesdays: Date[] = [];
      const d = new Date(year, month, 1);
      while (d.getDay() !== 3) d.setDate(d.getDate() + 1);
      while (d.getMonth() === month) {
        wednesdays.push(new Date(d));
        d.setDate(d.getDate() + 7);
      }
      for (const idx of [1, 3]) {
        const occ = wednesdays[idx];
        if (occ && occ >= today && dates.length < WEEKS_AHEAD) dates.push(toIso(occ));
      }
      month++;
      if (month > 11) { month = 0; year++; }
    }
    return dates;
  }

  const dow = weekdayFor(chapter);
  const d = new Date(today);
  while (d.getDay() !== dow) d.setDate(d.getDate() + 1);
  for (let i = 0; i < WEEKS_AHEAD; i++) {
    dates.push(toIso(d));
    d.setDate(d.getDate() + 7);
  }
  return dates;
};


interface MemberOption {
  id: string;
  name: string;
  company: string;
  chapter: string;
  additional_chapters: string[];
}

const AdminSpeakerSchedule = () => {
  const [chapter, setChapter] = useState<"washington" | "union">("washington");
  const { speakers, refetch } = useWeeklySpeakers(chapter);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [draftTopic, setDraftTopic] = useState<Record<string, string>>({});
  const [assignMember, setAssignMember] = useState<Record<string, string>>({});
  const [slotTypeDraft, setSlotTypeDraft] = useState<Record<string, SlotType>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const dates = useMemo(() => generateUpcomingDates(chapter), [chapter]);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, name, company, chapter, additional_chapters")
        .eq("is_hidden", false)
        .order("name");
      if (!error && data) setMembers(data as MemberOption[]);
    })();
  }, []);

  const eligibleMembers = useMemo(
    () =>
      members.filter(
        (m) =>
          m.chapter === chapter ||
          (m.additional_chapters || []).includes(chapter) ||
          m.chapter === "all"
      ),
    [members, chapter]
  );

  const getSlotType = (key: string): SlotType => slotTypeDraft[key] ?? "member";

  const handleCreate = async (date: string) => {
    const key = speakerKey(chapter, date);
    const slotType = getSlotType(key);
    setBusyKey(key);

    let res;
    if (slotType === "member") {
      const memberId = assignMember[key];
      if (!memberId) {
        toast.error("Select a member to assign.");
        setBusyKey(null);
        return;
      }
      res = await claimWeeklySlot({
        chapter,
        occurrenceDate: date,
        memberId,
        topic: draftTopic[key]?.trim() || null,
      });
    } else if (slotType === "guest") {
      const name = draftTopic[key]?.trim();
      if (!name) {
        toast.error("Enter the guest speaker's name.");
        setBusyKey(null);
        return;
      }
      res = await createNonMemberSlot({
        chapter,
        occurrenceDate: date,
        slotType: "guest",
        topic: name,
      });
    } else {
      res = await createNonMemberSlot({
        chapter,
        occurrenceDate: date,
        slotType: "breakout",
        topic: draftTopic[key]?.trim() || null,
      });
    }

    setBusyKey(null);
    if (res.error) {
      toast.error(
        res.error.code === "23505" ? "Slot already filled." : res.error.message
      );
    } else {
      toast.success(
        slotType === "member"
          ? "Speaker assigned."
          : slotType === "guest"
          ? "Guest speaker added."
          : "Breakout session set."
      );
      setDraftTopic((m) => ({ ...m, [key]: "" }));
      setAssignMember((m) => ({ ...m, [key]: "" }));
      setSlotTypeDraft((m) => ({ ...m, [key]: "member" }));
      refetch();
    }
  };

  const handleSaveTopic = async (slotId: string, key: string) => {
    setBusyKey(key);
    const { error } = await updateSpeakerTopic(slotId, draftTopic[key]?.trim() || null);
    setBusyKey(null);
    if (error) toast.error(error.message);
    else {
      toast.success("Saved.");
      refetch();
    }
  };

  const handleRelease = async (slotId: string, key: string) => {
    if (!confirm("Clear this slot?")) return;
    setBusyKey(key);
    const { error } = await releaseSpeakerSlot(slotId);
    setBusyKey(null);
    if (error) toast.error(error.message);
    else {
      toast.success("Slot cleared.");
      refetch();
    }
  };

  const topicPlaceholder = (st: SlotType) =>
    st === "guest"
      ? "Guest speaker name (required)"
      : st === "breakout"
      ? "Breakout topic (optional)"
      : "Optional topic";

  const slotLabel = (st: SlotType) =>
    st === "breakout" ? "Breakout Session" : st === "guest" ? "Guest Speaker" : "Member";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mic className="w-5 h-5" />
          Weekly Meeting Speaker Schedule
        </CardTitle>
        <CardDescription>
          Assign a member, add a guest speaker, or mark a date as a breakout session.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={chapter} onValueChange={(v) => setChapter(v as "washington" | "union")}>
          <TabsList>
            <TabsTrigger value="washington">Washington (2nd &amp; 4th Wed)</TabsTrigger>
            <TabsTrigger value="union">Union (Thu)</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Date</TableHead>
                <TableHead className="w-[200px]">Type / Speaker</TableHead>
                <TableHead>Topic</TableHead>
                <TableHead className="w-[180px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dates.map((date) => {
                const key = speakerKey(chapter, date);
                const slot = speakers[key];
                const draft = draftTopic[key] ?? "";
                const busy = busyKey === key;
                const newSlotType = getSlotType(key);

                return (
                  <TableRow key={key}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {format(new Date(`${date}T00:00:00`), "EEE, MMM d")}
                    </TableCell>
                    <TableCell>
                      {slot ? (
                        <div className="text-sm space-y-1">
                          <Badge variant={slot.slot_type === "member" ? "secondary" : "outline"}>
                            {slotLabel(slot.slot_type)}
                          </Badge>
                          {slot.slot_type === "member" && (
                            <>
                              <div className="font-medium">{slot.member?.name || "Member"}</div>
                              {slot.member?.company && (
                                <div className="text-xs text-muted-foreground">{slot.member.company}</div>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <Select
                            value={newSlotType}
                            onValueChange={(v) =>
                              setSlotTypeDraft((m) => ({ ...m, [key]: v as SlotType }))
                            }
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="member">Member Speaker</SelectItem>
                              <SelectItem value="guest">Guest Speaker</SelectItem>
                              <SelectItem value="breakout">Breakout Session</SelectItem>
                            </SelectContent>
                          </Select>
                          {newSlotType === "member" && (
                            <Select
                              value={assignMember[key] || ""}
                              onValueChange={(v) =>
                                setAssignMember((m) => ({ ...m, [key]: v }))
                              }
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Select member…" />
                              </SelectTrigger>
                              <SelectContent>
                                {eligibleMembers.map((m) => (
                                  <SelectItem key={m.id} value={m.id}>
                                    {m.name}
                                    {m.company ? ` — ${m.company}` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        value={slot ? draft || slot.topic || "" : draft}
                        onChange={(e) =>
                          setDraftTopic((m) => ({ ...m, [key]: e.target.value }))
                        }
                        placeholder={
                          slot ? topicPlaceholder(slot.slot_type) : topicPlaceholder(newSlotType)
                        }
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {slot ? (
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            disabled={busy || draft === "" || draft === (slot.topic ?? "")}
                            onClick={() => handleSaveTopic(slot.id, key)}
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-destructive hover:text-destructive"
                            disabled={busy}
                            onClick={() => handleRelease(slot.id, key)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => handleCreate(date)}
                        >
                          {newSlotType === "breakout" ? (
                            <Users className="w-3 h-3 mr-1" />
                          ) : (
                            <UserPlus className="w-3 h-3 mr-1" />
                          )}
                          {newSlotType === "breakout" ? "Set" : "Assign"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSpeakerSchedule;
